import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { CheckCircle2, Download, FileText, Send, ShieldCheck, Upload } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from '@/components/LocalizedLink'
import { CatalogMetadata } from '@/components/catalog'
import { languageFromPath, type RouteKey, getLocalizedPath } from '@/i18n/routes'
import { useOverlay } from '@/components/layout/OverlayProvider'
import { apiUrl } from '@/lib/api'

type InstitutionalKind = 'certifications' | 'careers' | 'privacy'

const routeForKind: Record<InstitutionalKind, RouteKey> = {
  certifications: 'certifications',
  careers: 'careers',
  privacy: 'privacy',
}

export function InstitutionalPage({ kind }: { kind: InstitutionalKind }) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const language = languageFromPath(location.pathname)
  const routeKey = routeForKind[kind]
  const canonicalPath = getLocalizedPath(routeKey, language)

  return <div className={`institutional-page institutional-page--${kind}`}>
    <CatalogMetadata
      language={language}
      title={t(`institutional.${kind}.title`)}
      description={t(`institutional.${kind}.metaDescription`)}
      canonicalPath={canonicalPath}
      alternatePaths={{ it: getLocalizedPath(routeKey, 'it'), en: getLocalizedPath(routeKey, 'en') }}
    />
    <header className="institutional-hero internal-hero">
      <div className="shell-container">
        <nav className="catalog-breadcrumb" aria-label={t('institutional.breadcrumbLabel')}>
          <ol><li><LocalizedLink to="home">{t('nav.home')}</LocalizedLink></li><li aria-current="page"><span>/</span><span>{t(`institutional.${kind}.title`)}</span></li></ol>
        </nav>
        <div className="institutional-heading"><h1 tabIndex={-1}>{t(`institutional.${kind}.title`)}</h1><p>{t(`institutional.${kind}.intro`)}</p></div>
      </div>
    </header>
    <main className="shell-container institutional-content">
      {kind === 'certifications' && <Certifications />}
      {kind === 'careers' && <Careers />}
      {kind === 'privacy' && <PrivacyContent />}
    </main>
  </div>
}

function Certifications() {
  const { t } = useTranslation('common')
  const documents = [
    { key: 'iso9001', file: 'ICIM-9001-010116-01_2023ESAMED-SRL.jpg' },
    { key: 'iso13485', file: 'ICIM-13485-010117-01_2023ESAMED-SRL.jpg' },
  ] as const
  return <>
    <section className="institutional-notice" aria-labelledby="certificate-status-title"><ShieldCheck aria-hidden="true" /><div><h2 id="certificate-status-title">{t('institutional.certifications.statusTitle')}</h2><p>{t('institutional.certifications.status')}</p></div></section>
    <section aria-labelledby="certificate-records-title"><h2 id="certificate-records-title">{t('institutional.certifications.recordsTitle')}</h2><div className="certificate-grid">
      {documents.map(({ key, file }) => <article className="certificate-record" key={key}>
        <img src={`/media/source/certificates/cert-${key === 'iso9001' ? '9001' : '13485'}.png`} width="300" height="225" alt={t(`institutional.certifications.${key}.imageAlt`)} />
        <div><h3>{t(`institutional.certifications.${key}.name`)}</h3><p>{t(`institutional.certifications.${key}.record`)}</p><a className="button button-secondary certificate-download" href={`/media/source/certificates/${file}`} download={file} aria-label={t('institutional.certifications.downloadImageLabel', { name: t(`institutional.certifications.${key}.name`) })}><Download aria-hidden="true" />{t('institutional.certifications.downloadImage')}</a></div>
      </article>)}
    </div></section>
  </>
}

function Careers() {
  const { t } = useTranslation('common')
  const fileInput = useRef<HTMLInputElement>(null)
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', message: '', privacyConsent: false })
  const [cv, setCv] = useState<File | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle')

  const validate = () => {
    const next: Record<string, string> = {}
    if (form.firstName.trim().length < 2 || form.firstName.length > 100) next.firstName = t('institutional.careers.form.errors.firstName')
    if (form.lastName.trim().length < 2 || form.lastName.length > 100) next.lastName = t('institutional.careers.form.errors.lastName')
    if (!/^\S+@\S+\.\S+$/.test(form.email) || form.email.length > 254) next.email = t('institutional.careers.form.errors.email')
    if (form.phone.length > 50) next.phone = t('institutional.careers.form.errors.phone')
    if (form.message.length > 4000) next.message = t('institutional.careers.form.errors.message')
    if (cv && (cv.size > 5 * 1024 * 1024 || (!['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(cv.type) && !/\.(pdf|doc|docx)$/i.test(cv.name)))) next.cv = t('institutional.careers.form.errors.cv')
    if (!form.privacyConsent) next.privacyConsent = t('institutional.careers.form.errors.privacy')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const change = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? (event.target as HTMLInputElement).checked : value }))
    setErrors((current) => ({ ...current, [name]: '' }))
    if (status !== 'idle') setStatus('idle')
  }

  const changeCv = (event: ChangeEvent<HTMLInputElement>) => {
    setCv(event.target.files?.[0] ?? null)
    setErrors((current) => ({ ...current, cv: '' }))
    if (status !== 'idle') setStatus('idle')
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!validate()) return
    setStatus('pending')
    const payload = new FormData()
    Object.entries(form).forEach(([key, value]) => payload.append(key, String(value)))
    if (cv) payload.append('cv', cv)
    try {
      const response = await fetch(apiUrl('/api/careers'), { method: 'POST', body: payload })
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok || typeof body !== 'object' || body === null || (body as { success?: unknown }).success !== true) throw new Error('invalid-careers-response')
      setStatus('success')
      setForm({ firstName: '', lastName: '', phone: '', email: '', message: '', privacyConsent: false })
      setCv(null)
      if (fileInput.current) fileInput.current.value = ''
    } catch {
      setStatus('error')
    }
  }

  const statusText = status === 'success' ? t('institutional.careers.form.success') : status === 'error' ? t('institutional.careers.form.providerError') : ''

  return <div className="careers-layout">
    <aside className="careers-brief" aria-labelledby="careers-form-title">
      <h2 id="careers-form-title">{t('institutional.careers.form.title')}</h2>
      <p>{t('institutional.careers.form.intro')}</p>
      <div className="careers-file-note"><FileText aria-hidden="true" /><p>{t('institutional.careers.form.fileNote')}</p></div>
    </aside>
    <section className="careers-form-panel" aria-label={t('institutional.careers.form.title')}>
      <p className="careers-required-note">{t('institutional.careers.form.requiredNote')}</p>
      <form noValidate onSubmit={submit}>
        <div className="careers-form-grid">
          <CareerField name="firstName" label={t('institutional.careers.form.firstName')} required error={errors.firstName}><input id="careers-firstName" name="firstName" value={form.firstName} maxLength={100} autoComplete="given-name" onChange={change} aria-invalid={Boolean(errors.firstName)} aria-describedby={errors.firstName ? 'careers-firstName-error' : undefined} /></CareerField>
          <CareerField name="lastName" label={t('institutional.careers.form.lastName')} required error={errors.lastName}><input id="careers-lastName" name="lastName" value={form.lastName} maxLength={100} autoComplete="family-name" onChange={change} aria-invalid={Boolean(errors.lastName)} aria-describedby={errors.lastName ? 'careers-lastName-error' : undefined} /></CareerField>
          <CareerField name="phone" label={t('institutional.careers.form.phone')} error={errors.phone}><input id="careers-phone" name="phone" type="tel" value={form.phone} maxLength={50} autoComplete="tel" onChange={change} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'careers-phone-error' : undefined} /></CareerField>
          <CareerField name="email" label={t('institutional.careers.form.email')} required error={errors.email}><input id="careers-email" name="email" type="email" value={form.email} maxLength={254} autoComplete="email" onChange={change} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'careers-email-error' : undefined} /></CareerField>
        </div>
        <CareerField name="cv" label={t('institutional.careers.form.cv')} error={errors.cv}>
          <div className="careers-file-control"><Upload aria-hidden="true" /><input ref={fileInput} id="careers-cv" name="cv" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={changeCv} aria-invalid={Boolean(errors.cv)} aria-describedby={errors.cv ? 'careers-cv-error' : 'careers-cv-help'} /></div>
          <span id="careers-cv-help" className="field-help">{t('institutional.careers.form.cvHelp')}</span>
        </CareerField>
        <CareerField name="message" label={t('institutional.careers.form.message')} error={errors.message}><textarea id="careers-message" name="message" rows={6} maxLength={4000} value={form.message} onChange={change} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'careers-message-error' : undefined} /></CareerField>
        <div className="privacy-field careers-privacy">
          <input id="careers-privacyConsent" name="privacyConsent" type="checkbox" checked={form.privacyConsent} onChange={change} aria-invalid={Boolean(errors.privacyConsent)} aria-describedby={errors.privacyConsent ? 'careers-privacyConsent-error' : undefined} />
          <label htmlFor="careers-privacyConsent">{t('institutional.careers.form.privacy')} <LocalizedLink to="privacy">{t('institutional.careers.form.privacyLink')}</LocalizedLink></label>
        </div>
        {errors.privacyConsent && <p id="careers-privacyConsent-error" className="field-error">{errors.privacyConsent}</p>}
        <div className="careers-submit-row"><button className="button" type="submit" disabled={status === 'pending'}><Send aria-hidden="true" />{status === 'pending' ? t('institutional.careers.form.sending') : t('institutional.careers.form.send')}</button><p className={`careers-status${status === 'error' ? ' careers-status--error' : ''}`} role="status" aria-live="polite">{status === 'success' && <CheckCircle2 aria-hidden="true" />}{statusText}</p></div>
      </form>
    </section>
  </div>
}

function CareerField({ name, label, error, required = false, children }: { name: string; label: string; error?: string; required?: boolean; children: ReactNode }) {
  return <div className={`careers-field careers-field--${name}`}><label htmlFor={`careers-${name}`}>{label}{required && <span aria-hidden="true"> *</span>}</label>{children}{error && <p id={`careers-${name}-error`} className="field-error">{error}</p>}</div>
}

function PrivacyContent() {
  const { t } = useTranslation('common')
  const { open } = useOverlay()
  const sections = ['controller', 'data', 'purposes', 'retention', 'cookies', 'sharing', 'rights'] as const
  return <div className="privacy-layout">
    <nav className="privacy-index" aria-label={t('institutional.privacy.indexLabel')}>
      <p>{t('institutional.privacy.indexTitle')}</p>
      <ol>{sections.map((section) => <li key={section}><a href={`#privacy-${section}`}>{t(`institutional.privacy.${section}.title`)}</a></li>)}</ol>
    </nav>
    <article className="privacy-document">
      <p className="privacy-updated">{t('institutional.privacy.updated')}</p>
      {sections.map((section, index) => <section id={`privacy-${section}`} key={section} aria-labelledby={`privacy-${section}-title`}>
        <h2 id={`privacy-${section}-title`}><span aria-hidden="true">{index + 1}.</span> {t(`institutional.privacy.${section}.title`)}</h2>
        <p>{t(`institutional.privacy.${section}.body`)}</p>
        {section === 'controller' && <address>{t('brand.company')}<br />{t('contact.address')}<br />{t('contact.city')}<br /><a href={`mailto:${t('contact.email')}`}>{t('contact.email')}</a></address>}
        {section === 'cookies' && <div className="privacy-cookie-details">
          <div><h3>{t('institutional.privacy.cookies.necessaryTitle')}</h3><p>{t('institutional.privacy.cookies.necessaryBody')}</p></div>
          <div><h3>{t('institutional.privacy.cookies.optionalTitle')}</h3><p>{t('institutional.privacy.cookies.optionalBody')}</p></div>
          <button className="button button-secondary" onClick={() => open('cookies')}>{t('cookie.preferencesTitle')}</button>
        </div>}
      </section>)}
    </article>
  </div>
}
